import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import type { ReactNode } from "react";

import { Button } from "../components/Button";
import { Card, Skeleton } from "../components/Display";
import { Icon } from "../components/Icon";
import { Input } from "../components/Input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/Misc";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../components/Overlays";
import { cn } from "../lib/cn";
import { EmptyState } from "./Page";

export type { ColumnDef, SortingState };

export interface DataTableLabels {
  previous: string;
  next: string;
  pageOf: (page: number, pages: number, total: number) => string;
  emptyTitle: string;
  emptyDescription?: string;
}

const defaultLabels: DataTableLabels = {
  previous: "Previous",
  next: "Next",
  pageOf: (page, pages, total) =>
    `Page ${page} of ${pages} · ${total.toLocaleString("en-IN")} items`,
  emptyTitle: "Nothing here yet",
};

export interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  /** Total rows on the server (for pagination). */
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  loading?: boolean;
  getRowId?: (row: T) => string;
  onRowClick?: (row: T) => void;
  labels?: Partial<DataTableLabels>;
  /** Shown instead of the default empty state. */
  empty?: ReactNode;
  className?: string;
}

/**
 * Server-side table: the API does paging/sorting/filtering; the table renders one page.
 * Sorting maps to the API's `?ordering=` (e.g. [{id: "created_at", desc: true}] → "-created_at").
 */
export function DataTable<T>({
  columns,
  data,
  total,
  page,
  pageSize,
  onPageChange,
  sorting = [],
  onSortingChange,
  loading = false,
  getRowId,
  onRowClick,
  labels: labelOverrides,
  empty,
  className,
}: DataTableProps<T>) {
  const labels = { ...defaultLabels, ...labelOverrides };
  const pages = Math.max(1, Math.ceil(total / pageSize));
  // TanStack Table v8 isn't React-Compiler-safe; we don't use the compiler, so this is fine.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getRowId,
    state: { sorting },
    manualSorting: true,
    manualPagination: true,
    pageCount: pages,
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      onSortingChange?.(next);
    },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Card className={cn("overflow-hidden", className)}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id} className="hover:bg-transparent">
              {group.headers.map((header) => {
                const sortable = header.column.getCanSort() && Boolean(onSortingChange);
                const direction = header.column.getIsSorted();
                return (
                  <TableHead
                    key={header.id}
                    aria-sort={
                      direction === "asc"
                        ? "ascending"
                        : direction === "desc"
                          ? "descending"
                          : undefined
                    }
                  >
                    {header.isPlaceholder ? null : sortable ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="inline-flex items-center gap-1 uppercase hover:text-foreground"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <Icon
                          name="chevron-down"
                          size={14}
                          className={cn(
                            "transition-transform",
                            !direction && "opacity-30",
                            direction === "asc" && "rotate-180",
                          )}
                        />
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {loading
            ? Array.from({ length: Math.min(pageSize, 6) }, (_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full max-w-40" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={cn(onRowClick && "cursor-pointer")}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
        </TableBody>
      </Table>
      {!loading &&
        data.length === 0 &&
        (empty ?? <EmptyState title={labels.emptyTitle} description={labels.emptyDescription} />)}
      <div className="flex flex-col items-center justify-between gap-3 border-t border-border px-4 py-3 text-sm sm:flex-row">
        <span className="text-muted-foreground">{labels.pageOf(page, pages, total)}</span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => onPageChange(page - 1)}
          >
            <Icon name="chevron-left" size={16} />
            {labels.previous}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pages || loading}
            onClick={() => onPageChange(page + 1)}
          >
            {labels.next}
            <Icon name="chevron-right" size={16} />
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** Converts table sorting to the API's `ordering` query param. */
export function sortingToOrdering(sorting: SortingState): string | undefined {
  return sorting.length ? sorting.map((s) => (s.desc ? `-${s.id}` : s.id)).join(",") : undefined;
}

// ── FilterBar ────────────────────────────────────────────────────────

export interface FilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Filter controls (Selects, toggles…). */
  children?: ReactNode;
  onClear?: () => void;
  clearLabel?: string;
  className?: string;
}

export function FilterBar({
  search,
  onSearchChange,
  searchPlaceholder = "Search…",
  children,
  onClear,
  clearLabel = "Clear filters",
  className,
}: FilterBarProps) {
  return (
    <div
      className={cn("mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center", className)}
    >
      <div className="sm:w-80">
        <Input
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          startIcon={<Icon name="search" size={16} />}
        />
      </div>
      {/* each filter (Select, date input…) gets a compact fixed width next to the search field */}
      {children && (
        <div className="flex flex-col gap-2 *:w-full sm:flex-row sm:flex-wrap sm:items-center sm:*:w-44">
          {children}
        </div>
      )}
      {onClear && (
        <Button variant="ghost" size="sm" onClick={onClear} className="sm:ml-auto">
          {clearLabel}
        </Button>
      )}
    </div>
  );
}

// ── DetailDrawer ─────────────────────────────────────────────────────

export interface DetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  closeLabel?: string;
}

/** Side panel for viewing/editing one record without leaving the list. */
export function DetailDrawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  closeLabel,
}: DetailDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent closeLabel={closeLabel}>
        <div className="border-b border-border px-6 py-5 pr-14">
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <div className="flex justify-end gap-3 border-t border-border px-6 py-4">{footer}</div>
        )}
      </SheetContent>
    </Sheet>
  );
}
